import { reactQueryClient } from '@/utils/reactQueryClient';

/** Schedules delayed invalidateQueries ticks until `shouldStop` or delays exhaust. */
export function scheduleDelayedQueryInvalidation({
  queryKey,
  delaysMs,
  shouldStop,
  activeCleanupRef,
}: {
  queryKey: readonly unknown[];
  delaysMs: readonly number[];
  shouldStop: () => boolean;
  activeCleanupRef: { current: (() => void) | null };
}): () => void {
  activeCleanupRef.current?.();
  activeCleanupRef.current = null;

  const timerIds: ReturnType<typeof setTimeout>[] = [];
  let cancelled = false;

  const cleanup = (): void => {
    if (cancelled) return;
    cancelled = true;
    timerIds.forEach(clearTimeout);
    if (activeCleanupRef.current === cleanup) {
      activeCleanupRef.current = null;
    }
  };

  const maybeStop = (): void => {
    if (cancelled) return;
    if (shouldStop()) cleanup();
  };

  for (const delayMs of delaysMs) {
    timerIds.push(
      setTimeout(() => {
        void (async () => {
          if (cancelled) return;
          maybeStop();
          if (cancelled) return;
          try {
            await reactQueryClient.invalidateQueries({ queryKey });
          } finally {
            if (!cancelled) maybeStop();
          }
        })();
      }, delayMs)
    );
  }

  activeCleanupRef.current = cleanup;
  return cleanup;
}

export function cancelDelayedQueryInvalidation(activeCleanupRef: {
  current: (() => void) | null;
}): void {
  activeCleanupRef.current?.();
  activeCleanupRef.current = null;
}
